import React, { useMemo, useState } from 'react'
import {
  DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS,
  Pagination,
  PAGINATION_PREVIEW_CONFIG,
  PAGINATION_PREVIEW_STATE_LABELS,
  PAGINATION_PREVIEW_STATES,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './PaginationShowcase.css'

const PaginationShowcase: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [totalPages, setTotalPages] = useState(12)
  const [disabled, setDisabled] = useState(false)
  const [darkMode, setDarkMode] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Content',
        fields: [
          {
            id: 'pagination-current-page',
            label: 'Current page',
            control: (
              <input
                id="pagination-current-page"
                className="ui-field-control"
                type="number"
                min={1}
                max={totalPages}
                value={currentPage}
                onChange={(event) =>
                  setCurrentPage(
                    Math.min(
                      totalPages,
                      Math.max(1, Number(event.target.value) || 1),
                    ),
                  )
                }
              />
            ),
          },
          {
            id: 'pagination-total-pages',
            label: 'Total pages',
            control: (
              <input
                id="pagination-total-pages"
                className="ui-field-control"
                type="number"
                min={1}
                value={totalPages}
                onChange={(event) => {
                  const nextTotal = Math.max(1, Number(event.target.value) || 1)
                  setTotalPages(nextTotal)
                  setCurrentPage((page) => Math.min(page, nextTotal))
                }}
              />
            ),
          },
          {
            id: 'pagination-page-size',
            label: 'Page size',
            control: (
              <select
                id="pagination-page-size"
                className="ui-field-control"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'pagination-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="pagination-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'pagination-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="pagination-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [currentPage, darkMode, disabled, pageSize, totalPages],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Pagination"
        description="Page navigation with results-per-page control, first/previous/next/last actions, and ellipsis for long page ranges."
        stageClassName="ui-preview__stage--wide"
      >
        <div
          className={`pagination-showcase__sheet${
            darkMode ? ' pagination-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Interactive pagination used by the Data table. Adjust page, total
              pages, and page size from the properties panel.
            </p>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              disabled={disabled}
              darkMode={darkMode}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </section>

          <section className="pagination-showcase__matrix">
            {PAGINATION_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`pagination-showcase__matrix-row${
                  darkMode ? ' pagination-showcase__matrix-row--dark' : ''
                }`}
              >
                <h4 className="pagination-showcase__matrix-label">
                  {PAGINATION_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <Pagination
                  {...PAGINATION_PREVIEW_CONFIG[previewState]}
                  previewState={previewState}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Pagination"
        sections={propertySections}
      />
    </>
  )
}

export default PaginationShowcase
